/**
 * Mutations for the [reporters store]{link module:store/reporters}
 *
 * @module store/reporters/mutations
 */

import { Reporter, ReporterConfig } from '@/store/modules/reporters'

/**
 * Mutation to change the users of a reporter
 *
 * Adds or removes a user from the store.
 * Added users are enhanced by asEmail and asFcm as false
 *
 * @function
 *
 * @params
 * @param {integer} params.id is the reporterId
 * @param Array{object} users with user as {id, email}
 *
 */
export function CHANGE_USERS(state, { id, users }) {
  if (!state[id]) return

  const deletedIds = Object.keys(state[id].users).filter((x) => !users.map((x) => x.id).includes(x))

  for (const userId of deletedIds) delete state[id].users[userId]

  for (const user of users) {
    if (!state[id].users[user.id]) {
      state[id].users[user.id] = {
        asEmail: false,
        asFcm: false,
        email: user.email,
        id: user.id
      }
    }

    if (state[id].users[id]) continue
  }
}

/**
 * Mutation to create a reporter
 *
 * Creates a Reporter from params and adds it to the store
 *
 * @function
 *
 * @params
 * @param {integer} params.id is the reporterId
 * @param {integer} params.emsId
 * @param {integer} params.groupId
 * @param {string} params.name
 * @param {string} params.type {sensor | logEvent}
 * @param {boolean} params.notifyOnConditionNoLongerFulfilled
 * @param {boolean} params.disabled
 * @param {object} params.currentStatus { emsId => TRIGGERED | NOT_TRIGGERED}
 * @param {ReporterConfig} params.config
 * @param {object} parms.users { userId => {id, email, asEmail, asFcm}}
 *
 */
export function CREATE_REPORTER(
  state,
  {
    id,
    emsId = null,
    groupId = null,
    name = '',
    type = null,
    notifyOnConditionNoLongerFulfilled = false,
    disabled = false,
    currentStatus = null,
    subscribed = null,
    config = null,
    users = {}
  }
) {
  config = config ?? new ReporterConfig({})

  state[id] = new Reporter({
    id,
    emsId,
    groupId,
    name,
    type,
    notifyOnConditionNoLongerFulfilled,
    disabled,
    currentStatus,
    subscribed,
    config,
    users
  })
}

/**
 * Mutation to set a reporter
 *
 * @function
 *
 * @params
 * @param {Reporter} params.reporter
 *
 */
export function SET_REPORTER(state, { reporter }) {
  state[reporter.id] = reporter
}

/**
 * Mutation to set parameters of a reporter
 *
 * @function
 *
 * @params
 * @param {integer} params.id
 * @param {object} params.params
 *
 */
export function UPDATE_REPORTER(state, { id, params }) {
  for (const param in params) {
    if (param === 'users') {
      state[id].users = JSON.parse(JSON.stringify(params.users))
    } else {
      state[id][param] = params[param]
    }
  }
}

/**
 * Mutation to set parameters of a reporter-config
 *
 * @function
 *
 * @params
 * @param {integer} params.id
 * @param {object} params.params
 *
 */
export function UPDATE_REPORTER_CONFIG(state, { id, params }) {
  if (!state[id]?.config) return

  for (const param in params) {
    if (param === 'devices') {
      state[id].config.devices = JSON.parse(JSON.stringify(params.devices))
    } else {
      state[id].config[param] = params[param]
    }
  }
}

/**
 * Mutation to set parameters of a user in a reporter
 *
 * @function
 *
 * @params
 * @param {integer} params.reporterId
 * @param {integer} params.userId
 * @param {object} params.params
 *
 */
export function UPDATE_USER_SETTING(state, { reporterId, userId, params }) {
  if (!state?.[reporterId]?.users?.[userId]) return

  for (const param in params) {
    state[reporterId].users[userId][param] = params[param]
  }
}

/**
 * Mutation to change a medium of a user in a reporter
 *
 * @function
 *
 * @params
 * @param {integer} params.reporterId
 * @param {integer} params.userId
 * @param {string} params.medium {asEmail|asFcm}
 * @param {boolean} params.value
 *
 */
export function CHANGE_MEDIUM(state, { reporterId, userId, medium, value }) {
  if (state?.[reporterId]?.users?.[userId]) {
    state[reporterId].users[userId][medium] = value
  }
}

/**
 * Mutation to add a user to a reporter
 *
 * @function
 *
 * @params
 * @param {integer} params.reporterId
 * @param {object} params.user {id, email, asEmail, asFcm}
 *
 */
export function SUBSCRIBE(state, { reporterId, user }) {
  if (state?.[reporterId]?.users) {
    state[reporterId].users[user.id] = user
  }
}

/**
 * Mutation to remove a user from a reporter
 *
 * @function
 *
 * @params
 * @param {integer} params.reporterId
 * @param {integer} params.userId
 *
 */
export function UNSUBSCRIBE(state, { reporterId, userId }) {
  if (state?.[reporterId]?.users?.[userId]) {
    delete state[reporterId].users[userId]
  }
}

/**
 * Mutation to reset the reporters
 *
 * @function
 *
 */
export function RESET_REPORTERS(state) {
  for (const id in state) {
    delete state[id]
  }
}
